"use client"
import { useState, useEffect } from "react"
import { useLeaderboard } from "../../contexts/LeaderboardContext"
import { validateUsername } from "../../utils/profanity-filter"
import { cleanupProfaneEntries, auditProfaneEntries } from "../../utils/cleanup-leaderboard"
import { trackScoreSubmission } from "../../utils/analytics"
import "../../App.css"

const LeaderboardModal = ({
  onClose,
  mode,
  completionTime,
  isDailyChallenge,
  memoryMode,
  difficulty,
  moveCount,
  formatTime,
  viewOnly = false, // Add a viewOnly prop with default value of false
  alreadySubmitted = false, // Add prop to indicate if the game was already submitted
}) => {
  const { leaderboard, isLoading: leaderboardLoading, error, fetchLeaderboard, submitScore } = useLeaderboard()
  const [playerName, setPlayerName] = useState(() => localStorage.getItem("lastPlayerName") || "")
  const [submitted, setSubmitted] = useState(alreadySubmitted)
  const [submitError, setSubmitError] = useState(null)
  const [difficultyFilter, setDifficultyFilter] = useState(null)
  const [currentMode, setCurrentMode] = useState(isDailyChallenge ? "daily" : memoryMode ? "memory" : "standard")
  const [leaderboardLimit, setLeaderboardLimit] = useState(20) // Increased limit to show more entries
  const [isLoading, setIsLoading] = useState(false)

  // Check if this game has already been submitted
  useEffect(() => {
    const checkIfSubmitted = () => {
      if (!completionTime) return false

      // Generate a game ID similar to how it's done in submitScore
      const gameId = `${localStorage.getItem("lastPlayerName") || ""}-${currentMode}-${difficulty}-${completionTime.toFixed(6)}`
      const submittedGames = JSON.parse(localStorage.getItem("submittedGames") || "[]")

      // Check for exact match
      if (submittedGames.includes(gameId)) {
        return true
      }

      // Check for similar games (same player, mode, difficulty with very close time)
      const similarGameExists = submittedGames.some((id) => {
        const parts = id.split("-")
        return (
          parts[0] === (localStorage.getItem("lastPlayerName") || "") &&
          parts[1] === currentMode &&
          parts[2] === difficulty &&
          Math.abs(Number.parseFloat(parts[3]) - completionTime) < 0.1 // Allow for tiny differences in time
        )
      })

      return similarGameExists
    }

    if (checkIfSubmitted() && !submitted) {
      setSubmitted(true)
      setSubmitError("This game has already been submitted to the leaderboard")
    }
  }, [completionTime, currentMode, difficulty, submitted])

  // Load leaderboard data when component mounts or when filter changes
  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        if (process.env.NODE_ENV === "development") console.log(
          `Loading leaderboard for mode: ${currentMode}, filter: ${difficultyFilter || "none"}, limit: ${leaderboardLimit}`,
        )
        await fetchLeaderboard(currentMode, leaderboardLimit, difficultyFilter)
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.error("Error in fetchLeaderboard:", err)
      }
    }

    loadLeaderboard()
  }, [fetchLeaderboard, currentMode, difficultyFilter, leaderboardLimit, submitted]) // Added submitted as a dependency

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!playerName.trim()) return

    // Validate username
    const validation = validateUsername(playerName)
    if (!validation.valid) {
      setSubmitError(validation.error)
      return
    }

    setSubmitError(null)
    setIsLoading(true)

    try {
      // Save player name for future reference
      localStorage.setItem("lastPlayerName", validation.username)

      const success = await submitScore(validation.username, completionTime, currentMode, difficulty, moveCount)

      if (success) {
        setSubmitted(true)

        // Track score submission
        trackScoreSubmission(validation.username, currentMode, difficulty, completionTime, moveCount);

        // Refresh the leaderboard after submission with a slight delay to ensure Firebase has updated
        setTimeout(async () => {
          await fetchLeaderboard(currentMode, leaderboardLimit, difficultyFilter)
          setIsLoading(false)
        }, 1000)
      } else {
        setSubmitError("Failed to submit score. Please try again.")
        setIsLoading(false)
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Error submitting score:", err)
      setSubmitError(err.message || "An error occurred while submitting your score")
      setIsLoading(false)
    }
  }

  // Admin cleanup functions
  const handleCleanupProfanity = async () => {
    if (!window.confirm("Are you sure you want to remove all profane entries from the leaderboard? This cannot be undone.")) {
      return
    }
    
    setIsLoading(true)
    try {
      const result = await cleanupProfaneEntries()
      if (result.success) {
        alert(`Successfully removed ${result.deletedCount} profane entries!`)
        // Refresh the leaderboard
        await fetchLeaderboard(currentMode, leaderboardLimit, difficultyFilter)
      } else {
        alert(`Cleanup failed: ${result.error}`)
      }
    } catch (error) {
      alert(`Cleanup error: ${error.message}`)
    }
    setIsLoading(false)
  }

  const handleAuditProfanity = async () => {
    setIsLoading(true)
    try {
      const result = await auditProfaneEntries()
      if (result.success) {
        if (result.count === 0) {
          alert("No profane entries found! 🎉")
        } else {
          alert(`Found ${result.count} profane entries. Check console for details.`)
        }
      } else {
        alert(`Audit failed: ${result.error}`)
      }
    } catch (error) {
      alert(`Audit error: ${error.message}`)
    }
    setIsLoading(false)
  }

  // Custom format time function that ensures we display the correct milliseconds
  const formatTimeWithPrecision = (timeInSeconds) => {
    // Ensure timeInSeconds is a number
    const time = typeof timeInSeconds === "string" ? Number.parseFloat(timeInSeconds) : timeInSeconds

    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)

    // Get milliseconds with 3 decimal places
    const milliseconds = Math.floor((time % 1) * 1000)

    const formattedMinutes = minutes > 0 ? `${minutes}:` : ""
    const formattedSeconds = `${seconds < 10 && minutes > 0 ? "0" : ""}${seconds}`
    const formattedMilliseconds = milliseconds.toString().padStart(3, "0")

    return {
      mainPart: `${formattedMinutes}${formattedSeconds}`,
      millisPart: formattedMilliseconds,
    }
  }

  // Function to show more entries
  const loadMoreEntries = () => {
    setLeaderboardLimit((prev) => prev + 10)
  }

  // Get the appropriate title based on the current mode
  const getLeaderboardTitle = () => {
    if (currentMode === "daily") return "Daily Challenge Leaderboard"
    if (currentMode === "memory") return "Memory Mode Leaderboard"
    return "Leaderboard"
  }

  // Add a function to display a message when the game has already been submitted
  const renderSubmissionForm = () => {
    // If view-only mode is enabled, don't show the form
    if (viewOnly) {
      return null
    }

    // If already submitted, show confirmation
    if (submitted) {
      return <p className="score-submitted-text">Score submitted!</p>
    }

    // If no completion time (game not finished), don't show form
    if (completionTime === null) {
      return null
    }

    // Otherwise show the submission form
    return (
      <form onSubmit={handleSubmit} className="score-submit-form">
        <input
          type="text"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          placeholder="Your name"
          maxLength="20"
          required
          className="player-name-input"
        />
        <button type="submit" className="submit-score-btn" disabled={isLoading}>
          {isLoading ? "Submitting..." : "Submit Score"}
        </button>
      </form>
    )
  }

  return (
    <div className="modal-overlay">
      <div className="leaderboard-modal">
        <button onClick={onClose} className="close-button">
          ×
        </button>
        <h2>{getLeaderboardTitle()}</h2>
        {currentMode === "standard" && (
          <div className="filter-options">
            <span className="filter-label">Filter by:</span>
            <div className="filter-buttons">
              <button
                className={`filter-btn easy ${difficultyFilter === "easy" ? "active" : ""}`}
                onClick={() => setDifficultyFilter(difficultyFilter === "easy" ? null : "easy")}
              >
                Easy
              </button>
              <button
                className={`filter-btn medium ${difficultyFilter === "medium" ? "active" : ""}`}
                onClick={() => setDifficultyFilter(difficultyFilter === "medium" ? null : "medium")}
              >
                Medium
              </button>
              <button
                className={`filter-btn hard ${difficultyFilter === "hard" ? "active" : ""}`}
                onClick={() => setDifficultyFilter(difficultyFilter === "hard" ? null : "hard")}
              >
                Hard
              </button>
              {difficultyFilter && (
                <button className="filter-btn clear" onClick={() => setDifficultyFilter(null)}>
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
        {difficultyFilter && (
          <div className="active-filter">
            <span>
              Filtered by: <strong>{difficultyFilter}</strong>
            </span>
            <button onClick={() => setDifficultyFilter(null)}>Clear Filter</button>
          </div>
        )}
        {error && (
          <div className="error-text">
            <p>Error loading leaderboard: {error}</p>
            {error.includes("index") && (
              <p>
                <a
                  href="https://console.firebase.google.com/v1/r/project/matcha-5e965/firestore/indexes?"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="index-link"
                >
                  Create index here
                </a>
              </p>
            )}
          </div>
        )}
        {submitError && <p className="error-text">Error: {submitError}</p>}

        {leaderboardLoading || isLoading ? (
          <p className="loading-text">Loading...</p>
        ) : (
          <div className="leaderboard-content">
            {leaderboard && leaderboard.length > 0 ? (
              <>
                <div className={`leaderboard-header ${currentMode === "daily" ? "daily-header" : ""}`}>
                  <span>Rank</span>
                  <span>Player</span>
                  {currentMode === "daily" && <span>Moves</span>}
                  <span>Time</span>
                </div>
                <div className="leaderboard-entries">
                  {leaderboard.map((entry, index) => (
                    <div
                      key={entry.id || index}
                      className={`leaderboard-entry ${currentMode === "daily" ? "daily-entry" : ""}`}
                    >
                      <span className={index < 3 ? "entry-rank-top" : "entry-rank-normal"}>{index + 1}</span>
                      <span className="entry-name">
                        {index === 0 ? '👑 ' : ''}
                        {entry.playerName}
                        {entry.mode === "standard" && entry.difficulty && (
                          <span
                            className={`difficulty-badge ${entry.difficulty} ${difficultyFilter === entry.difficulty ? "active" : ""}`}
                            onClick={() =>
                              setDifficultyFilter(difficultyFilter === entry.difficulty ? null : entry.difficulty)
                            }
                          >
                            {entry.difficulty}
                          </span>
                        )}
                      </span>
                      {currentMode === "daily" && <span className="entry-moves">{entry.moves || "-"}</span>}
                      <span className="entry-time">
                        {formatTimeWithPrecision(entry.time).mainPart}.
                        <span className="entry-time-ms">{formatTimeWithPrecision(entry.time).millisPart}</span>
                      </span>
                    </div>
                  ))}
                </div>
                {leaderboard.length >= leaderboardLimit && (
                  <button onClick={loadMoreEntries} className="load-more-btn">
                    Load More
                  </button>
                )}
              </>
            ) : (
              <p className="no-scores-text">No scores yet. Be the first!</p>
            )}
          </div>
        )}

        {renderSubmissionForm()}

        {/* Admin panel - only show in development */}
        {process.env.NODE_ENV === "development" && (
          <div className="admin-panel" style={{ 
            marginTop: "20px", 
            padding: "15px", 
            border: "1px solid #666", 
            borderRadius: "5px", 
            backgroundColor: "#1a1a1a" 
          }}>
            <h4 style={{ color: "#ff6b6b", fontSize: "0.9rem", marginBottom: "10px" }}>
              Admin Tools (Dev Only)
            </h4>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button 
                onClick={handleAuditProfanity}
                disabled={isLoading}
                style={{
                  padding: "5px 10px",
                  fontSize: "0.8rem",
                  backgroundColor: "#4a90e2",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer"
                }}
              >
                Audit Profanity
              </button>
              <button 
                onClick={handleCleanupProfanity}
                disabled={isLoading}
                style={{
                  padding: "5px 10px",
                  fontSize: "0.8rem",
                  backgroundColor: "#e74c3c",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer"
                }}
              >
                Clean Up Profanity
              </button>
            </div>
          </div>
        )}

        <button onClick={onClose} className="close-leaderboard-btn">
          Close
        </button>
      </div>
    </div>
  )
}

export default LeaderboardModal

